import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../../lib/apiClient';
import { useToast } from '../../hooks/use-toast';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Briefcase, Plus, Trash2, Loader2, Edit2, X } from 'lucide-react';

const ProjectsManager = () => {
  const { toast } = useToast();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    description: '',
    tagsText: '',
    category: 'Full-Stack',
    featured: false,
    status: 'Completed',
    year: '',
    duration: '',
    role: '',
    problem: '',
    solution: '',
    impact: '',
    techStackText: '',
    featuresText: '',
    challengesText: '',
    github: '',
    demo: '',
    order_num: 0,
  });

  const parseList = (value) =>
    value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);

  const serializeList = (value = []) => value.join(', ');

  const parseChallenges = (value) => {
    const lines = value
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);

    return lines.map((line) => {
      const [challenge, solution] = line.split('|');
      if (!challenge || !solution) {
        throw new Error('Each challenge must be formatted as "Challenge | Solution".');
      }
      return { challenge: challenge.trim(), solution: solution.trim() };
    });
  };

  const serializeChallenges = (value = []) =>
    value.map((item) => `${item.challenge} | ${item.solution}`).join('\n');

  const loadProjects = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiClient.get('/api/admin/projects');
      setProjects(data || []);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load projects', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  const handleEdit = (project) => {
    setEditingProject(project);
    setFormData({
      title: project.title || '',
      subtitle: project.subtitle || '',
      description: project.description || '',
      tagsText: serializeList(project.tags || []),
      category: project.category || 'Full-Stack',
      featured: Boolean(project.featured),
      status: project.status || 'Completed',
      year: project.year || '',
      duration: project.duration || '',
      role: project.role || '',
      problem: project.problem || '',
      solution: project.solution || '',
      impact: project.impact || '',
      techStackText: serializeList(project.tech_stack || []),
      featuresText: serializeList(project.features || []),
      challengesText: serializeChallenges(project.challenges || []),
      github: project.github || '',
      demo: project.demo || '',
      order_num: project.order_num ?? 0,
    });
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditingProject(null);
    setFormData({
      title: '',
      subtitle: '',
      description: '',
      tagsText: '',
      category: 'Full-Stack',
      featured: false,
      status: 'Completed',
      year: '',
      duration: '',
      role: '',
      problem: '',
      solution: '',
      impact: '',
      techStackText: '',
      featuresText: '',
      challengesText: '',
      github: '',
      demo: '',
      order_num: 0,
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        title: formData.title,
        subtitle: formData.subtitle,
        description: formData.description,
        tags: parseList(formData.tagsText),
        category: formData.category,
        featured: Boolean(formData.featured),
        status: formData.status,
        year: formData.year,
        duration: formData.duration,
        role: formData.role,
        problem: formData.problem,
        solution: formData.solution,
        impact: formData.impact,
        tech_stack: parseList(formData.techStackText),
        features: parseList(formData.featuresText),
        challenges: parseChallenges(formData.challengesText),
        github: formData.github?.trim() || null,
        demo: formData.demo?.trim() || null,
        order_num: Number.parseInt(formData.order_num, 10) || 0,
      };
      if (editingProject) {
        await apiClient.put(`/api/admin/projects/${editingProject.id}`, payload);
        toast({ title: 'Success', description: 'Project updated' });
      } else {
        await apiClient.post('/api/admin/projects', payload);
        toast({ title: 'Success', description: 'Project created' });
      }
      await loadProjects();
      handleCancel();
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Operation failed', variant: 'destructive' });
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this project?')) return;
    try {
      await apiClient.delete(`/api/admin/projects/${id}`);
      toast({ title: 'Success', description: 'Project deleted' });
      await loadProjects();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete', variant: 'destructive' });
    }
  };

  const newProjectTemplate = {
    title: 'New Project',
    subtitle: '',
    description: '',
    tagsText: '',
    category: 'Full-Stack',
    featured: false,
    status: 'Completed',
    year: '2025',
    duration: '',
    role: '',
    problem: '',
    solution: '',
    impact: '',
    techStackText: '',
    featuresText: '',
    challengesText: '',
    github: '',
    demo: '',
    order_num: 0,
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-cyan-500" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="projects-manager">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Briefcase className="w-8 h-8 text-cyan-500" />
          <h1 className="text-3xl font-bold text-white">Projects Management</h1>
        </div>
        {!isEditing && (
          <Button
            onClick={() => {
              setFormData(newProjectTemplate);
              setIsEditing(true);
            }}
            className="bg-cyan-500 hover:bg-cyan-600 text-black"
          >
            <Plus className="w-4 h-4 mr-2" />Add Project
          </Button>
        )}
      </div>

      {isEditing && (
        <div className="bg-neutral-900 border border-cyan-500/50 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-white">{editingProject ? 'Edit Project' : 'Add New Project'}</h2>
            <Button onClick={handleCancel} variant="ghost" size="sm"><X className="w-5 h-5" /></Button>
          </div>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm text-neutral-300">Title</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="bg-neutral-800 border-neutral-700 text-white"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm text-neutral-300">Subtitle</label>
                <Input
                  value={formData.subtitle}
                  onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                  className="bg-neutral-800 border-neutral-700 text-white"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm text-neutral-300">Description</label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="bg-neutral-800 border-neutral-700 text-white"
                rows={4}
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <label className="text-sm text-neutral-300">Category</label>
                <Input
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="bg-neutral-800 border-neutral-700 text-white"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm text-neutral-300">Status</label>
                <Input
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="bg-neutral-800 border-neutral-700 text-white"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm text-neutral-300">Year</label>
                <Input
                  value={formData.year}
                  onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                  className="bg-neutral-800 border-neutral-700 text-white"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <label className="text-sm text-neutral-300">Duration</label>
                <Input
                  value={formData.duration}
                  onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                  className="bg-neutral-800 border-neutral-700 text-white"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm text-neutral-300">Role</label>
                <Input
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="bg-neutral-800 border-neutral-700 text-white"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm text-neutral-300">Order</label>
                <Input
                  type="number"
                  value={formData.order_num}
                  onChange={(e) => setFormData({ ...formData, order_num: e.target.value })}
                  className="bg-neutral-800 border-neutral-700 text-white"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm text-neutral-300">Tags (comma separated)</label>
                <Input
                  value={formData.tagsText}
                  onChange={(e) => setFormData({ ...formData, tagsText: e.target.value })}
                  className="bg-neutral-800 border-neutral-700 text-white"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm text-neutral-300">Featured</label>
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={formData.featured}
                    onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                    className="h-4 w-4"
                  />
                  <span className="text-sm text-neutral-400">Show as featured</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm text-neutral-300">Tech Stack (comma separated)</label>
                <Textarea
                  value={formData.techStackText}
                  onChange={(e) => setFormData({ ...formData, techStackText: e.target.value })}
                  className="bg-neutral-800 border-neutral-700 text-white"
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm text-neutral-300">Features (comma separated)</label>
                <Textarea
                  value={formData.featuresText}
                  onChange={(e) => setFormData({ ...formData, featuresText: e.target.value })}
                  className="bg-neutral-800 border-neutral-700 text-white"
                  rows={3}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm text-neutral-300">Problem</label>
                <Textarea
                  value={formData.problem}
                  onChange={(e) => setFormData({ ...formData, problem: e.target.value })}
                  className="bg-neutral-800 border-neutral-700 text-white"
                  rows={3}
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm text-neutral-300">Solution</label>
                <Textarea
                  value={formData.solution}
                  onChange={(e) => setFormData({ ...formData, solution: e.target.value })}
                  className="bg-neutral-800 border-neutral-700 text-white"
                  rows={3}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm text-neutral-300">Impact</label>
              <Textarea
                value={formData.impact}
                onChange={(e) => setFormData({ ...formData, impact: e.target.value })}
                className="bg-neutral-800 border-neutral-700 text-white"
                rows={3}
                required
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm text-neutral-300">Challenges (one per line)</label>
              <Textarea
                value={formData.challengesText}
                onChange={(e) => setFormData({ ...formData, challengesText: e.target.value })}
                className="bg-neutral-800 border-neutral-700 text-white"
                rows={4}
                placeholder="Scaling reads | Added caching layer"
              />
              <p className="text-xs text-neutral-500">Format: Challenge | Solution</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm text-neutral-300">GitHub URL</label>
                <Input
                  value={formData.github}
                  onChange={(e) => setFormData({ ...formData, github: e.target.value })}
                  className="bg-neutral-800 border-neutral-700 text-white"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm text-neutral-300">Demo URL</label>
                <Input
                  value={formData.demo}
                  onChange={(e) => setFormData({ ...formData, demo: e.target.value })}
                  className="bg-neutral-800 border-neutral-700 text-white"
                />
              </div>
            </div>

            <Button type="submit" className="bg-cyan-500 hover:bg-cyan-600 text-black">
              {editingProject ? 'Update' : 'Create'} Project
            </Button>
          </form>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4">
        {projects.map((project) => (
          <div key={project.id} className="bg-neutral-900 border border-neutral-800 rounded-xl p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-white mb-2">{project.title}</h3>
                <p className="text-neutral-400 text-sm mb-2">{project.subtitle}</p>
                <div className="text-xs text-neutral-500 space-y-1">
                  <p>Category: {project.category} | Status: {project.status}</p>
                  <p>{project.featured ? '⭐ Featured' : ''} | Order: {project.order_num}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => handleEdit(project)} variant="outline" size="sm"><Edit2 className="w-4 h-4" /></Button>
                <Button onClick={() => handleDelete(project.id)} variant="outline" size="sm" className="border-red-500/50 text-red-400"><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProjectsManager;
